<script lang="ts">
  import { api, ApiError, parseValidationErrors } from '$shared/api';
  import { addToast } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import { extractYoutubeId, youtubeThumbUrl } from '$lib/utils/youtube';
  import type { TrackPreview } from '$types/index';

  interface StyleOption {
    key: string;
    label: string;
  }

  interface GenreGroup {
    genre_id: number;
    genre_name: string;
    styles: StyleOption[];
  }

  // Duplicate-URL payload from the 409 `already_in_catalog` response.
  interface DupeInfo {
    existing_track_id: number;
    credited_to_you: boolean;
  }

  let url = $state('');
  let genres = $state<GenreGroup[]>([]);
  let genresLoading = $state(true);
  let selectedGenreId = $state<number | null>(null);
  let selectedStyle = $state<string | null>(null);

  let preview = $state<TrackPreview | null>(null);
  let previewLoading = $state(false);

  let submitting = $state(false);
  let fieldErrors = $state<Record<string, string>>({});
  let dupe = $state<DupeInfo | null>(null);
  let added = $state<{ title: string } | null>(null);

  const ytId = $derived(extractYoutubeId(url));
  const selectedGenre = $derived(genres.find((g) => g.genre_id === selectedGenreId) ?? null);
  const canSubmit = $derived(Boolean(ytId && preview?.ok && selectedGenreId !== null && selectedStyle && !submitting));

  $effect(() => {
    loadGenres();
  });

  async function loadGenres() {
    try {
      // Same consumption as AccountGenres: ?artists=0 skips the per-chip artist
      // hints (catalog add doesn't render them either).
      genres = await api.get<GenreGroup[]>('/users/styles?artists=0');
    } catch (e) {
      console.error('Failed to load genres:', e);
      addToast('Could not load genres. Reload and try again.', 'error');
    } finally {
      genresLoading = false;
    }
  }

  // Debounced server preview — fires 400ms after the last keystroke of a URL
  // that yields a valid video id. The local thumbnail (from the id alone)
  // renders immediately; the preview card fills in once the API answers.
  // A sequence counter drops stale responses when the URL keeps changing.
  let previewSeq = 0;
  $effect(() => {
    const id = ytId;
    const currentUrl = url.trim();
    const seq = ++previewSeq;
    preview = null;
    dupe = null;
    if (!id) {
      previewLoading = false;
      return;
    }
    previewLoading = true;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get<TrackPreview>(`/tracks/preview?url=${encodeURIComponent(currentUrl)}&type=youtube`);
        if (seq === previewSeq) preview = res;
      } catch {
        if (seq === previewSeq) preview = { ok: false, error: 'Could not load the preview. Try again.' };
      } finally {
        if (seq === previewSeq) previewLoading = false;
      }
    }, 400);
    return () => clearTimeout(timer);
  });

  function pickGenre(genreId: number) {
    if (selectedGenreId === genreId) return;
    selectedGenreId = genreId;
    // Styles belong to a genre — a genre switch invalidates the old pick.
    selectedStyle = null;
  }

  /**
   * Find the 409 `{existing_track_id, credited_to_you}` payload wherever the
   * envelope carries it (`data`, `error`, or top-level) — tolerant of how the
   * backend chooses to emit it.
   */
  function dupeFromBody(body: unknown): DupeInfo | null {
    if (!body || typeof body !== 'object') return null;
    const record = body as Record<string, unknown>;
    // The envelope emits it at error.details (docs/curation-duels.md § catalog).
    const errorDetails =
      record.error && typeof record.error === 'object' ? (record.error as Record<string, unknown>).details : undefined;
    for (const candidate of [errorDetails, record.data, record.error, record]) {
      if (candidate && typeof candidate === 'object' && 'existing_track_id' in candidate) {
        const c = candidate as { existing_track_id: number | string; credited_to_you?: boolean };
        return {
          existing_track_id: Number(c.existing_track_id) || 0,
          credited_to_you: Boolean(c.credited_to_you),
        };
      }
    }
    return null;
  }

  /** Locate the Yii validation-error list in a 422 body and map field → message. */
  function validationErrorsFromBody(body: unknown): Record<string, string> {
    if (Array.isArray(body)) return parseValidationErrors(body);
    if (body && typeof body === 'object') {
      const record = body as Record<string, unknown>;
      const nestedError =
        record.error && typeof record.error === 'object' ? (record.error as Record<string, unknown>) : null;
      for (const candidate of [record.data, nestedError?.errors]) {
        if (Array.isArray(candidate)) return parseValidationErrors(candidate);
      }
    }
    return {};
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!canSubmit) return;
    submitting = true;
    fieldErrors = {};
    dupe = null;
    try {
      await api.post('/catalog/tracks', {
        youtube_url: url.trim(),
        genre_id: selectedGenreId,
        style: selectedStyle,
      });
      added = { title: preview?.title || 'Your song' };
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        dupe = dupeFromBody(err.data) ?? { existing_track_id: 0, credited_to_you: false };
      } else if (err instanceof ApiError && err.status === 422) {
        const parsed = validationErrorsFromBody(err.data);
        if (Object.keys(parsed).length > 0) {
          fieldErrors = parsed;
        } else {
          addToast(err.message || 'That song could not be added.', 'error');
        }
      } else {
        addToast((err as Error).message || 'Something went wrong. Try again.', 'error');
      }
    } finally {
      submitting = false;
    }
  }

  // "Add another song" — clear the URL/preview but keep the genre + style
  // selection so back-to-back adds in the same style are one paste away.
  function addAnother() {
    added = null;
    dupe = null;
    fieldErrors = {};
    url = '';
    preview = null;
  }

  function formatDuration(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
</script>

<div class="page">
  <h1>Add a Song</h1>
  <h2 class="page-sub">Put a song in the catalog — players claim it into duels</h2>

  {#if added}
    <div class="success-card">
      <div class="success-card__icon">&#10003;</div>
      <div class="success-card__body">
        <div class="success-card__title">{added.title} is in the catalog.</div>
        <p class="success-card__sub">Players can now claim it into duels — when it wins, you get the creator crown.</p>
        <div class="success-card__actions">
          <button class="btn btn-primary" onclick={addAnother}>Add another song</button>
          <a href="#/catalog" class="btn btn-outline">View My Catalog</a>
          <a href="#/duels" class="btn btn-outline">Browse duels</a>
        </div>
      </div>
    </div>
  {:else}
    <form class="add-form" onsubmit={handleSubmit}>
      <label class="field-label">
        YouTube URL
        <input
          type="url"
          class="input"
          placeholder="https://www.youtube.com/watch?v=…"
          bind:value={url}
          autocomplete="off"
        />
      </label>
      {#if url.trim() && !ytId}
        <p class="field-error">That doesn't look like a YouTube link. Paste a watch, youtu.be, or Shorts URL.</p>
      {/if}
      {#if fieldErrors.youtube_url}
        <p class="field-error">{fieldErrors.youtube_url}</p>
      {/if}

      {#if dupe}
        <div class="dupe-card">
          {#if dupe.credited_to_you}
            <p class="dupe-card__title">This song is already in the catalog — and it's yours.</p>
            <p class="dupe-card__sub">You're already credited as its creator, so there's nothing to add.</p>
          {:else}
            <p class="dupe-card__title">This song is already in the catalog</p>
            <p class="dupe-card__sub">Someone beat you to it — but it's still in play for duels.</p>
          {/if}
          {#if dupe.existing_track_id}
            <a href="#/tracks/{dupe.existing_track_id}" class="dupe-card__link">View it in the catalog →</a>
          {/if}
        </div>
      {/if}

      {#if ytId}
        <div class="preview-card">
          <img class="preview-thumb" src={youtubeThumbUrl(ytId)} alt="" loading="lazy" />
          <div class="preview-meta">
            {#if previewLoading}
              <p class="preview-loading">Checking that video…</p>
            {:else if preview?.ok}
              <div class="preview-title">{preview.title}</div>
              {#if preview.author}
                <div class="preview-author">{preview.author}</div>
              {/if}
              {#if preview.duration_sec}
                <div class="preview-duration">{formatDuration(preview.duration_sec)}</div>
              {/if}
            {:else if preview}
              <p class="preview-error">{preview.error || 'That video could not be loaded.'}</p>
            {/if}
          </div>
        </div>
      {/if}

      <fieldset class="picker">
        <legend class="picker-legend">Genre</legend>
        {#if genresLoading}
          <LoadingSkeleton lines={2} />
        {:else if genres.length === 0}
          <p class="picker-empty">No genres available right now.</p>
        {:else}
          <div class="pills">
            {#each genres as group (group.genre_id)}
              <button
                type="button"
                class="pill genre-pill"
                class:pill--selected={selectedGenreId === group.genre_id}
                aria-pressed={selectedGenreId === group.genre_id}
                onclick={() => pickGenre(group.genre_id)}
              >
                {group.genre_name}
              </button>
            {/each}
          </div>
        {/if}
        {#if fieldErrors.genre_id}
          <p class="field-error">{fieldErrors.genre_id}</p>
        {/if}
      </fieldset>

      {#if selectedGenre}
        <fieldset class="picker">
          <legend class="picker-legend">Style</legend>
          {#if selectedGenre.styles.length === 0}
            <p class="picker-empty">No styles listed for {selectedGenre.genre_name}.</p>
          {:else}
            <div class="pills">
              {#each selectedGenre.styles as styleOption (styleOption.key)}
                <button
                  type="button"
                  class="pill style-pill"
                  class:pill--selected={selectedStyle === styleOption.key}
                  aria-pressed={selectedStyle === styleOption.key}
                  onclick={() => (selectedStyle = styleOption.key)}
                >
                  {styleOption.label}
                </button>
              {/each}
            </div>
          {/if}
          {#if fieldErrors.style}
            <p class="field-error">{fieldErrors.style}</p>
          {/if}
        </fieldset>
      {/if}

      <button type="submit" class="btn btn-primary" disabled={!canSubmit}>
        {submitting ? 'Adding…' : 'Add to Catalog'}
      </button>
    </form>
  {/if}
</div>

<style>
  .page {
    max-width: 600px;
    margin: 0 auto;
  }
  h1 {
    background: linear-gradient(135deg, var(--pink), var(--purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .page-sub {
    color: var(--cyan);
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 1px;
    margin-bottom: 1.5rem;
  }

  .add-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
  .field-label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
  .field-error {
    color: var(--error);
    font-size: 0.8rem;
    margin: -0.75rem 0 0;
  }

  .preview-card {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
  }
  .preview-thumb {
    width: 120px;
    height: 68px;
    object-fit: cover;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    flex-shrink: 0;
  }
  .preview-meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    justify-content: center;
  }
  .preview-title {
    font-weight: 600;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .preview-author {
    font-size: 0.85rem;
    color: var(--text-secondary);
  }
  .preview-duration {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .preview-loading {
    color: var(--text-muted);
    font-size: 0.85rem;
    margin: 0;
  }
  .preview-error {
    color: var(--error);
    font-size: 0.85rem;
    margin: 0;
  }

  .picker {
    border: none;
    padding: 0;
    margin: 0;
  }
  .picker-legend {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    padding: 0;
  }
  .picker-empty {
    font-size: 0.85rem;
    color: var(--text-muted);
    margin: 0;
  }
  .pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .pill {
    padding: 0.4rem 0.85rem;
    border-radius: 100px;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--text-secondary);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
  }
  .genre-pill:hover {
    border-color: var(--purple);
    color: var(--purple);
  }
  .style-pill:hover {
    border-color: var(--cyan);
    color: var(--cyan);
  }
  .genre-pill.pill--selected {
    background: var(--purple);
    border-color: var(--purple);
    color: #fff;
    font-weight: 600;
  }
  .style-pill.pill--selected {
    background: var(--cyan);
    border-color: var(--cyan);
    color: #000;
    font-weight: 600;
  }

  .dupe-card {
    padding: 1rem;
    background: rgba(255, 45, 120, 0.08);
    border: 1px solid rgba(255, 45, 120, 0.35);
    border-radius: var(--radius);
  }
  .dupe-card__title {
    font-weight: 600;
    color: var(--text);
    margin: 0 0 0.25rem;
  }
  .dupe-card__sub {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0 0 0.5rem;
  }
  .dupe-card__link {
    font-size: 0.85rem;
    color: var(--cyan);
  }
  .dupe-card__link:hover {
    color: var(--pink);
  }

  .success-card {
    display: flex;
    gap: 1rem;
    padding: 1.5rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    margin-bottom: 1.5rem;
  }
  .success-card__icon {
    font-size: 2rem;
    color: var(--cyan);
    flex-shrink: 0;
  }
  .success-card__title {
    font-weight: 600;
    color: var(--text);
    margin-bottom: 0.25rem;
  }
  .success-card__sub {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 1rem;
  }
  .success-card__actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
</style>
